import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';

const SIZES = {
  sm: 'h-9 px-4 text-[0.78rem]',
  md: 'h-11 px-5 text-[0.82rem]',
  lg: 'h-12 px-6 text-[0.86rem]',
};

const VARIANTS = {
  primary:
    'bg-[color:var(--rmh-ink)] text-[color:var(--rmh-canvas)] border border-transparent hover:bg-[color:var(--rmh-ink-2)] hover:-translate-y-[1px] active:translate-y-0',
  secondary:
    'bg-[color:var(--rmh-surface)] text-[color:var(--rmh-ink)] border border-[color:var(--rmh-border-strong)] hover:border-[color:var(--rmh-ink)] hover:bg-[color:var(--rmh-surface-2)]',
  onInk:
    'bg-[color:var(--rmh-canvas)] text-[color:var(--rmh-ink)] border border-transparent hover:bg-[color:var(--rmh-surface-2)] hover:-translate-y-[1px] active:translate-y-0',
  outlineOnInk:
    'bg-transparent text-[color:var(--rmh-canvas)] border border-[rgba(250,247,242,0.35)] hover:border-[color:var(--rmh-canvas)] hover:bg-[rgba(250,247,242,0.06)]',
};

const base =
  'inline-flex items-center justify-center gap-2 rounded-[12px] font-medium tracking-[0.02em] whitespace-nowrap transition-[background-color,border-color,color,transform,box-shadow] duration-300 disabled:opacity-55 disabled:pointer-events-none';

/** Premium CTA used for every conversion action on the site. */
export const CtaButton = ({
  to,
  href,
  onClick,
  children,
  variant = 'primary',
  size = 'md',
  className,
  arrow = false,
  testId,
  type = 'button',
  disabled = false,
  ...rest
}) => {
  const cls = cn(base, SIZES[size], VARIANTS[variant], className);
  const inner = (
    <>
      {children}
      {arrow ? (
        <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId} {...rest}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} data-testid={testId} {...rest}>
        {inner}
      </a>
    );
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cls}
      data-testid={testId}
      {...rest}
    >
      {inner}
    </button>
  );
};

/** Editorial text link with an animated hairline underline. */
export const TextLink = ({ to, href, children, className, testId, ...rest }) => {
  const cls = cn(
    'rmh-link-underline inline-flex items-center gap-1.5 text-[0.8rem] font-medium tracking-[0.06em] uppercase text-[color:var(--rmh-ink)]',
    className,
  );
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId} {...rest}>
        {children}
        <ArrowRight className="h-3 w-3" aria-hidden="true" />
      </Link>
    );
  }
  return (
    <a href={href} className={cls} data-testid={testId} {...rest}>
      {children}
      <ArrowRight className="h-3 w-3" aria-hidden="true" />
    </a>
  );
};

export default CtaButton;
